import { DocumentNode, useMutation } from "@apollo/client";

import FlashContext from "components/FlashMessages/FlashContext";
import useTypedContext from "hooks/useTypedContext";
import { ApiKey, ApiKeyInput, ApiKeyType } from "types/generated";
import { formatApiSecretFile } from "utils/apiSecret";
import { downloadFile } from "utils/file";

import { CREATE_API_KEY } from "./gql";

type UseCreateApiKeyOptions = {
  callback?: () => void;
  refetchQueries?: DocumentNode[];
};

const useCreateApiKey = ({ callback, refetchQueries }: UseCreateApiKeyOptions) => {
  const { onError, reportSuccess } = useTypedContext(FlashContext);

  const [createApiKey] = useMutation<{ apiKeyCreate: ApiKey }>(CREATE_API_KEY, {
    refetchQueries,
  });

  const onCreate = (input: ApiKeyInput) => {
    createApiKey({
      variables: {
        input,
      },
    })
      .then(({ data }) => {
        if (data) {
          const {
            apiKeyCreate: { name, id, type, secret },
          } = data;

          if (type === ApiKeyType.Secret) {
            const secretFile = formatApiSecretFile(secret, id);

            if (!secretFile) {
              return reportError({
                message:
                  "Something went wrong while API config generation. Please contact us at support or try to generate API config again.",
              });
            }

            downloadFile(`api-key-${name}.config`, secretFile);
            reportSuccess({
              title: "Downloading file credentials",
              message: `Your credentials file is being downloaded for Key ID: ${id}`,
            });
          } else if (type === ApiKeyType.Oidc) {
            reportSuccess({
              message: `API key successfully created`,
            });
          }
          callback?.();
        }
      })
      .catch(onError);
  };

  return { onCreate };
};

export default useCreateApiKey;
